<template>
	<section class="migration" v-if="initialized">

		<template v-if="isLoggedIn">
			<h1 v-html="$t('migration.verify.loggedInTitle')"/>
			<div class="migration__text" v-html="$t('migration.verify.loggedInText')" />
			<Button
				level="primary"
				type="button"
				@click.prevent="goToAccount"
				:text="$t('migration.verify.loggedInButton')"
			/>
		</template>
		<template v-else-if="!error && !isLoggedIn">
			<h1 v-html="$t('migration.title')"/>
			<div class="migration__wrapper">
				<div class="migration__column">
					<div class="migration__intro">
						<div class="migration__text" v-html="$t('migration.userBrandsText')" />
						<ul
							class="migration__brands migration__brands--user"
							v-if="userBrands.length"
						>
							<li
								v-for="brand in userBrands"
								:key="brand.code"
								class="migration__brand migration__brand--user"
								:class="{
									'migration__brand--current': brand.current
								}"
							>
								<component
									:is="iconComponent(`logo-simple-${brand.code}`)"
									class="migration__brand-logo"
								/>
							</li>
						</ul>
						<div class="migration__text" v-html="$t('migration.userBrandsSubText')" />
					</div>

					<div class="migration__highlights">
						<div class="migration__text migration__text--highlight" v-html="$t('migration.highlightText')" />
						<ul
							class="migration__brands migration__brands--all"
							v-if="migrationBrands.length"
						>
							<li
								v-for="brand in migrationBrands"
								:key="brand.code"
								class="migration__brand migration__brand--all"
								:class="{
									'migration__brand--current': brand.current
								}"
							>
								<component
									:is="iconComponent(`logo-simple-${brand.code}`)"
									class="migration__brand-logo"
								/>
							</li>
						</ul>
					</div>
				</div>
				<div class="migration__column">
					<div class="migration__advantages">
						<h2 v-html="$t('migration.advantagesTitle')"/>
						<div class="migration__text" v-html="$t('migration.advantagesText')" />
					</div>
					<div class="migration__questions">
						<h2 v-html="$t('migration.questionsTitle')"/>
						<div class="migration__text">
							<div v-html="$t('migration.questionsText')" />
							<Button
								level="tertiary"
								type="button"
								:text="$t('migration.questionsButton')"
								:href="$t('migration.questionsUrl')"
								arrow
							/>
						</div>
					</div>
				</div>

				<div class="migration__column migration__column--span">
					<div class="migration__actions">
						<GeneralMessage
							v-if="response.message"
							:message="response.message"
							:type="response.type"
							@close="response.message = ''"
						/>
						<ValidationObserver
							ref="form"
							v-slot="{ invalid, errors }"
							tag="form"
							@submit.prevent="onSubmit"
							novalidate
						>
							<ValidationProvider
								:rules="{ required: { allowFalse: false } }"
								name="checkTerms"
								tag="div"
								v-slot="{ invalid, errors, validated }"
								:customMessages="{ required: $t('login.errorTerms') }"
							>
								<FormInputCheckbox
									name="terms"
									value="checkTerms"
									v-model="form.checkTerms"
									required
									immediate
									:disabled="disabled"
								>
									<span v-html="$t('login.termsLabel')"/>
								</FormInputCheckbox>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-html="errors[0]"
								/>
							</ValidationProvider>

							<div class="migration__buttons">
								<Button
									type="reset"
									level="secondary"
									:text="$t('migration.cancelButton')"
									:disabled="disabled"
									@click="onCancel"
								/>
								<Button
									type="submit"
									level="primary"
									:text="$t('migration.activateButton')"
									:disabled="disabled"
								/>
							</div>
						</ValidationObserver>
						<div class="migration__options">
							<div class="migration__options-title" v-html="$t('migration.optionsTitle')"/>
							<Button
								level="tertiary"
								type="button"
								@click.prevent="onNew"
								:text="$t('migration.newButton')"
								arrow
							/>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<h1 v-html="$t('migration.errorTitle')"/>
			<GeneralMessage
				:message="error"
				type="error"
				stick
			/>
			<Button
				level="primary"
				type="button"
				@click.prevent="onCancel"
				:text="$t('migration.cancelButton')"
			/>
		</template>
	</section>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import veevalidate from "@/helpers/veevalidate"
import GenericMixin from '@/mixins/GenericVueMixin'
import FormInputCheckbox from '@/components/FormInputCheckbox'
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import leadingZero from '@/utils/leadingZero'
import sleep from '@/utils/sleep'
import {getUrlParams} from '@/utils/urlParams';
import htmlComponent from "htmlcomponent";


export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationObserver,
		ValidationProvider,
		FormInputCheckbox,
		Button,
		GeneralMessage,
	},
	data() {
		return {
			initialized: false,
			error: null,
			form: {
				checkTerms: false
			},
			response: {
				type: 'info',
				message: '',
			}
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters("migration", [
			"migrationEmail",
			"migrationBrands",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		userBrands() {
			return this.migrationBrands.filter(brand => brand.supportsMigrate && !brand.migrated && brand.hasAccount);
		},
		disabled() {
			return this.loading;
		}
	},
	methods: {
		async onSubmit() {
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const response = await this.$store.dispatch('migration/startMigration', {
						email: this.migrationEmail,
						checkTerms: this.form.checkTerms,
					});
					if (response.redirect && response.redirect.key) {
						return this.$store.dispatch('redirect', {
							name: response.redirect.key,
							params: response.redirect.params || {},
						})
					}
				} catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onSubmit(), retry: true })
          } else {
            let message = this.$t('forms.errors.generic')
            const data = err.response.data;

            if (data) {
              if (data.redirect && data.redirect.key) {
                return this.$store.dispatch('redirect', {
                  name: data.redirect.key,
                  params: data.redirect.params || {},
                })
              }

              if (data.errors && data.errors.checkTerms) {
                message = data.errors.checkTerms[0]
              } else {
                message = data.message
              }
            }

            this.response.message = message;
            this.response.type = 'error';
          }
				}
				this.loading = false;
			}
		},
		onCancel() {
			this.$store.dispatch('redirect', {name: 'home'});
		},
		onNew() {
			htmlComponent.send('overlay:open', {type: 'login', tab: 'register'});
		},
		goToAccount() {
			this.$store.dispatch('redirect', {name: 'account'});
		},
	},
	created() {
		this.loading = true;
	},
	async mounted() {
		this.loading = true;

		const params = getUrlParams();
		if (!params.email) {
			this.error = this.$t('forms.errors.generic');
		}
		else {
			try {
				await this.$store.dispatch('migration/getBrands', { email: params.email });
			}
			catch(err) {
        if (err.isMaintenanceError) {
          this.onMaintenanceMode()
        } else {
          this.error = err.response.data.message;
        }
			}
		}

		this.initialized = true;
		this.loading = false;
	}
};
</script>

<style>
</style>
